import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  X, Plus, Filter, Trash2, Send, Loader2, BarChart3, LineChart as LineIcon,
  PieChart as PieIcon, Sparkles, Table as TableIcon, ChevronDown, Search as SearchIcon,
  Wand2, Eraser, FileDown, FileSpreadsheet, GitMerge, LayoutGrid, Bookmark, Save,
} from "lucide-react";
import { toast } from "sonner";
import api, { downloadBase64 } from "../lib/api";
import Markdown from "../lib/Markdown";

const COLORS = ["#10B981", "#F59E0B", "#3B82F6", "#8B5CF6", "#EC4899", "#14B8A6", "#F43F5E", "#A3E635"];
const OP_LABEL = {
  in: "is any of", not_in: "is none of", eq: "equals", ne: "not equal", contains: "contains",
  not_contains: "not contains", starts_with: "starts with", ends_with: "ends with",
  gt: ">", gte: "≥", lt: "<", lte: "≤", between: "between", is_empty: "is blank", not_empty: "not blank",
  date_eq: "on date", before: "before", after: "after", date_between: "date between",
  year: "year", quarter: "quarter", month: "month",
};
const NO_VALUE = ["is_empty", "not_empty"];

function defaultOp(type) {
  if (type === "numeric") return "gt";
  if (type === "datetime") return "date_between";
  return "in";
}
function defaultVal(op) {
  if (["in", "not_in"].includes(op)) return [];
  if (["between", "date_between"].includes(op)) return ["", ""];
  return "";
}

function ValuePicker({ options, selected, onChange }) {
  const [q, setQ] = useState("");
  const filtered = options.filter((o) => o.value.toLowerCase().includes(q.toLowerCase()));
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  return (
    <div className="bg-[#0B0F17] border border-white/10 rounded-lg p-2" data-testid="value-picker">
      <div className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-md px-2 mb-1.5">
        <SearchIcon size={12} className="text-slate-500" />
        <input data-testid="value-picker-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search values…"
          className="flex-1 bg-transparent py-1.5 text-xs text-slate-200 focus:outline-none" />
      </div>
      <div className="flex items-center justify-between text-[10px] text-slate-500 px-1 mb-1">
        <button data-testid="value-picker-select-all" onClick={() => onChange(filtered.map((o) => o.value))} className="hover:text-emerald-300">Select all</button>
        <button data-testid="value-picker-clear" onClick={() => onChange([])} className="hover:text-emerald-300">Clear</button>
      </div>
      <div className="max-h-44 overflow-y-auto space-y-0.5">
        {filtered.length === 0 && <div className="text-[11px] text-slate-600 px-1 py-2">No values</div>}
        {filtered.map((o) => (
          <label key={o.value} className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-white/5 cursor-pointer text-xs text-slate-300">
            <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} className="accent-emerald-500" />
            <span className="flex-1 truncate">{o.value}</span>
            <span className="text-[10px] text-slate-600">{o.count}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function chipText(f) {
  if (["in", "not_in"].includes(f.operator)) {
    const arr = Array.isArray(f.value) ? f.value : [];
    return `${f.column} ${OP_LABEL[f.operator]} [${arr.slice(0, 3).join(", ")}${arr.length > 3 ? ` +${arr.length - 3}` : ""}]`;
  }
  if (NO_VALUE.includes(f.operator)) return `${f.column} ${OP_LABEL[f.operator]}`;
  if (["between", "date_between"].includes(f.operator)) return `${f.column} ${OP_LABEL[f.operator]} ${f.value?.[0]}–${f.value?.[1]}`;
  return `${f.column} ${OP_LABEL[f.operator]} ${f.value}`;
}

export default function DataStudio({ dataset, onClose, datasets, onFiltersChange, onJoined }) {
  const [detail, setDetail] = useState(null);
  const [filters, setFilters] = useState([]);
  const [logic, setLogic] = useState("AND");
  const [dimension, setDimension] = useState("");
  const [measure, setMeasure] = useState("");
  const [agg, setAgg] = useState("sum");
  const [order, setOrder] = useState("desc");
  const [topN, setTopN] = useState(10);
  const [chartType, setChartType] = useState("bar");
  const [result, setResult] = useState(null);
  const [question, setQuestion] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisRes, setAnalysisRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [nlText, setNlText] = useState("");
  const [nlBusy, setNlBusy] = useState(false);
  const [openPicker, setOpenPicker] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pivot, setPivot] = useState({ rows: "", cols: "", value: "", agg: "sum", result: null, busy: false });
  const [joinCfg, setJoinCfg] = useState({ open: false, rightId: "", leftKey: "", rightKey: "", how: "inner", busy: false });
  const [views, setViews] = useState([]);
  const [viewName, setViewName] = useState("");
  const [activeSheet, setActiveSheet] = useState(dataset.activeSheet || (dataset.sheets || [])[0]);
  const sheets = dataset.sheets || [];

  const switchSheet = async (name) => {
    if (name === activeSheet) return;
    try {
      setBusy(true);
      await api.switchSheet(dataset.datasetId, name);
      setActiveSheet(name);
      setFilters([]); setResult(null); setAnalysisRes(null); setPivot((p) => ({ ...p, result: null }));
      const d = await api.datasetDetail(dataset.datasetId);
      setDetail(d);
      setDimension((d.analysis?.dimensions || [])[0] || "");
      setMeasure((d.analysis?.measures || [])[0] || "");
      toast.success(`Switched to sheet "${name}"`);
    } catch (e) { toast.error("Could not switch sheet"); }
    finally { setBusy(false); }
  };

  const schema = detail?.analysis?.schema || dataset.analysis?.schema || [];
  const columnMeta = detail?.columnMeta || [];
  const dimensions = detail?.analysis?.dimensions || dataset.analysis?.dimensions || [];
  const measures = detail?.analysis?.measures || dataset.analysis?.measures || [];
  const distinct = detail?.distinctValues || {};

  const metaOf = (name) => columnMeta.find((c) => c.name === name) || { type: (schema.find((s) => s.name === name)?.type || "text"), operators: [] };

  useEffect(() => {
    (async () => {
      try {
        const d = await api.datasetDetail(dataset.datasetId);
        setDetail(d);
        setDimension((d.analysis?.dimensions || [])[0] || "");
        setMeasure((d.analysis?.measures || [])[0] || "");
        api.listViews(dataset.datasetId).then(setViews).catch(() => {});
      } catch (e) { toast.error("Could not load dataset details"); }
    })();
  }, [dataset.datasetId]);

  const runQuery = useCallback(async () => {
    try {
      setBusy(true);
      const r = await api.dataQuery({
        datasetId: dataset.datasetId, filters, logic, dimension, measure, agg,
        topN: Number(topN), order, chartType,
      });
      setResult(r);
    } catch (e) { toast.error(e?.response?.data?.detail || "Query failed"); }
    finally { setBusy(false); }
  }, [dataset.datasetId, filters, logic, dimension, measure, agg, topN, order, chartType]);

  useEffect(() => { runQuery(); }, [runQuery]);
  useEffect(() => { onFiltersChange?.(filters, logic); }, [filters, logic, onFiltersChange]);

  const doExport = async (format) => {
    try {
      setExporting(true);
      const res = await api.exportReport({
        datasetId: dataset.datasetId, format, title: `SSG AI — ${dataset.filename}`,
        filters, logic,
        result: result?.aggregation?.rows?.length ? { columns: result.table.columns, rows: result.table.rows } : null,
      });
      downloadBase64(res.base64, res.filename, res.mime);
      toast.success(`Downloaded ${res.filename}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Export failed"); }
    finally { setExporting(false); }
  };

  const runPivot = async () => {
    if (!pivot.rows || !pivot.value) { toast.message("Pick a row dimension and a value."); return; }
    try {
      setPivot((p) => ({ ...p, busy: true }));
      const r = await api.pivot({ datasetId: dataset.datasetId, filters, logic,
        rows: [pivot.rows], columns: pivot.cols ? [pivot.cols] : [], value: pivot.value, agg: pivot.agg });
      setPivot((p) => ({ ...p, result: r, busy: false }));
    } catch (e) { toast.error("Pivot failed"); setPivot((p) => ({ ...p, busy: false })); }
  };

  const runJoin = async () => {
    if (!joinCfg.rightId || !joinCfg.leftKey || !joinCfg.rightKey) { toast.message("Pick the second file and keys."); return; }
    const leftKeys = [joinCfg.leftKey, joinCfg.leftKey2].filter(Boolean);
    const rightKeys = [joinCfg.rightKey, joinCfg.rightKey2].filter(Boolean);
    if (leftKeys.length !== rightKeys.length) { toast.error("Provide matching key pairs."); return; }
    try {
      setJoinCfg((j) => ({ ...j, busy: true }));
      const r = await api.join({ leftId: dataset.datasetId, rightId: joinCfg.rightId,
        leftKeys, rightKeys, how: joinCfg.how });
      const ms = r.matchStats;
      toast.success(`Joined → ${r.rowCount} rows on ${r.joinInfo.on} · matched ${ms.matched}, unmatched L${ms.leftUnmatched}/R${ms.rightUnmatched}`);
      onJoined?.(r);
    } catch (e) { toast.error(e?.response?.data?.detail || "Join failed"); }
    finally { setJoinCfg((j) => ({ ...j, busy: false })); }
  };

  const otherDatasets = (datasets || []).filter((d) => d.datasetId !== dataset.datasetId);

  const saveView = async () => {
    try {
      const state = { filters, logic, dimension, measure, agg, order, topN, chartType,
        pivot: { rows: pivot.rows, cols: pivot.cols, value: pivot.value, agg: pivot.agg } };
      await api.createView({ name: viewName || `View ${views.length + 1}`, datasetId: dataset.datasetId, filename: dataset.filename, state });
      setViewName("");
      setViews(await api.listViews(dataset.datasetId));
      toast.success("View saved");
    } catch (e) { toast.error("Could not save view"); }
  };
  const applyView = (v) => {
    const s = v.state || {};
    setFilters(s.filters || []); setLogic(s.logic || "AND");
    if (s.dimension !== undefined) setDimension(s.dimension);
    if (s.measure !== undefined) setMeasure(s.measure);
    if (s.agg) setAgg(s.agg); if (s.order) setOrder(s.order);
    if (s.topN) setTopN(s.topN); if (s.chartType) setChartType(s.chartType);
    if (s.pivot) setPivot((p) => ({ ...p, ...s.pivot }));
    toast.success(`Applied "${v.name}"`);
  };
  const deleteView = async (id) => {
    await api.deleteView(id);
    setViews(await api.listViews(dataset.datasetId));
  };

  const addFilter = () => {
    const col = (columnMeta[0]?.name) || (schema[0]?.name) || "";
    const type = metaOf(col).type;
    const op = defaultOp(type);
    setFilters((f) => [...f, { column: col, type, operator: op, value: defaultVal(op) }]);
  };
  const updateFilter = (i, patch) => setFilters((f) => f.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const removeFilter = (i) => setFilters((f) => f.filter((_, idx) => idx !== i));
  const clearFilters = () => setFilters([]);

  const onColChange = (i, col) => {
    const type = metaOf(col).type;
    const op = defaultOp(type);
    updateFilter(i, { column: col, type, operator: op, value: defaultVal(op) });
  };
  const onOpChange = (i, op) => updateFilter(i, { operator: op, value: defaultVal(op) });

  const applyNl = async () => {
    if (!nlText.trim()) return;
    try {
      setNlBusy(true);
      const r = await api.nlFilter({ datasetId: dataset.datasetId, text: nlText.trim() });
      if (!r.filters || r.filters.length === 0) { toast.message(r.note || "No filter derived."); return; }
      const withType = r.filters.map((f) => ({ ...f, type: metaOf(f.column).type }));
      setFilters(withType);
      setLogic(r.logic || "AND");
      toast.success(`Applied ${r.filters.length} filter(s) (${r.source}) — ${r.matchedRows?.toLocaleString()} of ${r.totalRows?.toLocaleString()} rows`);
      setNlText("");
    } catch (e) { toast.error(e?.response?.data?.detail || "NL filter failed"); }
    finally { setNlBusy(false); }
  };

  const ask = async () => {
    if (!question.trim()) return;
    try {
      setAnalyzing(true); setAnalysisRes(null);
      const r = await api.dataAnalyze({ datasetId: dataset.datasetId, question: question.trim(), filters, logic });
      setAnalysisRes(r);
    } catch (e) { toast.error(e?.response?.data?.detail || "Analysis failed"); }
    finally { setAnalyzing(false); }
  };

  const chartData = result?.chart?.data || [];
  const yKey = result?.chart?.yKey;

  const renderValueInput = (f, i) => {
    if (NO_VALUE.includes(f.operator)) return null;
    if (["in", "not_in"].includes(f.operator)) {
      const opts = distinct[f.column] || [];
      const sel = Array.isArray(f.value) ? f.value : [];
      return (
        <div className="relative">
          <button data-testid="filter-value-picker-toggle" onClick={() => setOpenPicker(openPicker === i ? null : i)}
            className="w-full flex items-center justify-between bg-white/5 border border-white/10 rounded-md px-2 py-1.5 text-xs text-slate-200">
            <span className="truncate">{sel.length ? `${sel.length} selected` : "Select values…"}</span>
            <ChevronDown size={13} className="text-slate-500" />
          </button>
          {openPicker === i && (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setOpenPicker(null)} />
              <div className="absolute z-30 mt-1 w-full">
                <ValuePicker options={opts} selected={sel} onChange={(v) => updateFilter(i, { value: v })} />
              </div>
            </>
          )}
        </div>
      );
    }
    if (f.type === "numeric") {
      if (f.operator === "between") {
        return (
          <div className="flex gap-1.5">
            <input data-testid="filter-value-min" type="number" placeholder="min" value={f.value?.[0] ?? ""} className="ds-fi"
              onChange={(e) => updateFilter(i, { value: [e.target.value, f.value?.[1] ?? ""] })} />
            <input data-testid="filter-value-max" type="number" placeholder="max" value={f.value?.[1] ?? ""} className="ds-fi"
              onChange={(e) => updateFilter(i, { value: [f.value?.[0] ?? "", e.target.value] })} />
          </div>
        );
      }
      return <input data-testid="filter-value" type="number" value={f.value} placeholder="value" className="ds-fi" onChange={(e) => updateFilter(i, { value: e.target.value })} />;
    }
    if (f.type === "datetime") {
      if (f.operator === "date_between") {
        return (
          <div className="flex gap-1.5">
            <input type="date" value={f.value?.[0] ?? ""} className="ds-fi" onChange={(e) => updateFilter(i, { value: [e.target.value, f.value?.[1] ?? ""] })} />
            <input type="date" value={f.value?.[1] ?? ""} className="ds-fi" onChange={(e) => updateFilter(i, { value: [f.value?.[0] ?? "", e.target.value] })} />
          </div>
        );
      }
      if (["date_eq", "before", "after"].includes(f.operator))
        return <input data-testid="filter-value" type="date" value={f.value} className="ds-fi" onChange={(e) => updateFilter(i, { value: e.target.value })} />;
      if (f.operator === "month")
        return <select data-testid="filter-value" value={f.value} className="ds-fi" onChange={(e) => updateFilter(i, { value: e.target.value })}>
          <option value="" className="bg-[#111827]">month…</option>
          {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m, idx) => <option key={m} value={idx + 1} className="bg-[#111827]">{m}</option>)}
        </select>;
      if (f.operator === "quarter")
        return <select data-testid="filter-value" value={f.value} className="ds-fi" onChange={(e) => updateFilter(i, { value: e.target.value })}>
          <option value="" className="bg-[#111827]">quarter…</option>{[1,2,3,4].map((q) => <option key={q} value={q} className="bg-[#111827]">Q{q}</option>)}
        </select>;
      return <input data-testid="filter-value" type="number" placeholder="year" value={f.value} className="ds-fi" onChange={(e) => updateFilter(i, { value: e.target.value })} />;
    }
    // text ops
    return <input data-testid="filter-value" value={f.value} placeholder="value" className="ds-fi" onChange={(e) => updateFilter(i, { value: e.target.value })} />;
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0B0F17] flex flex-col" data-testid="data-studio">
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-white/10 backdrop-blur-xl bg-[#0B0F17]/80">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-400"><BarChart3 size={18} /></div>
          <div className="min-w-0">
            <div className="font-head font-bold text-white truncate">{dataset.filename}</div>
            <div className="text-[11px] text-slate-500">
              {(detail?.analysis || dataset.analysis)?.rowCount?.toLocaleString()} rows · {(detail?.analysis || dataset.analysis)?.columnCount} cols · {(detail?.analysis || dataset.analysis)?.inferredDomain} · Quality {(detail?.analysis || dataset.analysis)?.dataQualityScore}/100
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {sheets.length > 1 && (
            <div className="flex items-center gap-1.5" data-testid="sheet-switcher">
              <span className="text-[11px] text-slate-500 hidden sm:inline">Sheet:</span>
              <select data-testid="sheet-select" value={activeSheet} onChange={(e) => switchSheet(e.target.value)}
                className="bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-emerald-500/40">
                {sheets.map((s) => <option key={s} value={s} className="bg-[#111827]">{s}</option>)}
              </select>
            </div>
          )}
          <button data-testid="close-data-studio" onClick={onClose} className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-slate-300 hover:text-white"><X size={18} /></button>
        </div>
      </div>
      <div className="px-4 sm:px-6 py-2 border-b border-white/10 flex items-center gap-2 flex-wrap">
        <span className="text-[11px] text-slate-500">Export current view:</span>
        <button data-testid="export-pdf-button" onClick={() => doExport("pdf")} disabled={exporting} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-300 hover:text-white hover:border-emerald-500/40 disabled:opacity-40"><FileDown size={13} /> PDF</button>
        <button data-testid="export-xlsx-button" onClick={() => doExport("xlsx")} disabled={exporting} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-300 hover:text-white hover:border-emerald-500/40 disabled:opacity-40"><FileSpreadsheet size={13} /> Excel</button>
        <button data-testid="export-docx-button" onClick={() => doExport("docx")} disabled={exporting} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-300 hover:text-white hover:border-emerald-500/40 disabled:opacity-40"><FileDown size={13} /> Word</button>
        <button data-testid="export-pptx-button" onClick={() => doExport("pptx")} disabled={exporting} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-300 hover:text-white hover:border-emerald-500/40 disabled:opacity-40"><FileDown size={13} /> PPT</button>
        <button data-testid="export-csv-button" onClick={() => doExport("csv")} disabled={exporting} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-300 hover:text-white hover:border-emerald-500/40 disabled:opacity-40"><FileDown size={13} /> CSV</button>
        <button data-testid="export-md-button" onClick={() => doExport("md")} disabled={exporting} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-300 hover:text-white hover:border-emerald-500/40 disabled:opacity-40"><FileDown size={13} /> MD</button>
        {exporting && <Loader2 size={14} className="animate-spin text-slate-400" />}
        {otherDatasets.length > 0 && (
          <button data-testid="combine-files-button" onClick={() => setJoinCfg((j) => ({ ...j, open: !j.open }))} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-300 hover:text-white hover:border-emerald-500/40 ml-auto"><GitMerge size={13} /> Combine files</button>
        )}
      </div>

      {joinCfg.open && otherDatasets.length > 0 && (
        <div className="px-4 sm:px-6 py-3 border-b border-white/10 bg-[#0B0F17]" data-testid="join-panel">
          <div className="text-sm font-semibold text-white mb-2 flex items-center gap-2"><GitMerge size={14} className="text-emerald-400" /> Cross-file join</div>
          <div className="grid sm:grid-cols-5 gap-2">
            <select data-testid="join-right-file" className="ds-fi" value={joinCfg.rightId} onChange={(e) => setJoinCfg((j) => ({ ...j, rightId: e.target.value }))}>
              <option value="" className="bg-[#111827]">Second file…</option>
              {otherDatasets.map((d) => <option key={d.datasetId} value={d.datasetId} className="bg-[#111827]">{d.filename}</option>)}
            </select>
            <select data-testid="join-left-key" className="ds-fi" value={joinCfg.leftKey} onChange={(e) => setJoinCfg((j) => ({ ...j, leftKey: e.target.value }))}>
              <option value="" className="bg-[#111827]">Key in {dataset.filename}…</option>
              {(dataset.analysis?.schema || []).map((c) => <option key={c.name} value={c.name} className="bg-[#111827]">{c.name}</option>)}
            </select>
            <select data-testid="join-right-key" className="ds-fi" value={joinCfg.rightKey} onChange={(e) => setJoinCfg((j) => ({ ...j, rightKey: e.target.value }))}>
              <option value="" className="bg-[#111827]">Key in second file…</option>
              {(otherDatasets.find((d) => d.datasetId === joinCfg.rightId)?.analysis?.schema || []).map((c) => <option key={c.name} value={c.name} className="bg-[#111827]">{c.name}</option>)}
            </select>
            <select className="ds-fi" value={joinCfg.how} onChange={(e) => setJoinCfg((j) => ({ ...j, how: e.target.value }))}>
              {["inner", "left", "right", "outer"].map((h) => <option key={h} value={h} className="bg-[#111827]">{h} join</option>)}
            </select>
            <button data-testid="run-join-button" onClick={runJoin} disabled={joinCfg.busy} className="rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-40">
              {joinCfg.busy ? <Loader2 size={15} className="animate-spin" /> : <GitMerge size={15} />} Join
            </button>
          </div>
          <div className="grid sm:grid-cols-3 gap-2 mt-2">
            <div className="text-[11px] text-slate-500 flex items-center">Optional 2nd key pair (multi-key join):</div>
            <select data-testid="join-left-key2" className="ds-fi" value={joinCfg.leftKey2 || ""} onChange={(e) => setJoinCfg((j) => ({ ...j, leftKey2: e.target.value }))}>
              <option value="" className="bg-[#111827]">—</option>
              {(dataset.analysis?.schema || []).map((c) => <option key={c.name} value={c.name} className="bg-[#111827]">{c.name}</option>)}
            </select>
            <select data-testid="join-right-key2" className="ds-fi" value={joinCfg.rightKey2 || ""} onChange={(e) => setJoinCfg((j) => ({ ...j, rightKey2: e.target.value }))}>
              <option value="" className="bg-[#111827]">—</option>
              {(otherDatasets.find((d) => d.datasetId === joinCfg.rightId)?.analysis?.schema || []).map((c) => <option key={c.name} value={c.name} className="bg-[#111827]">{c.name}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* Active filter bar */}
      <div className="px-4 sm:px-6 py-2.5 border-b border-white/10 bg-[#0B0F17] flex items-center gap-2 flex-wrap">
        <span className="text-[11px] text-slate-500 flex items-center gap-1"><Filter size={12} /> Active:</span>
        {filters.length === 0 && <span className="text-[11px] text-slate-600">no filters — showing all {dataset.analysis?.rowCount?.toLocaleString()} rows</span>}
        {filters.map((f, i) => (
          <span key={i} data-testid="active-filter-chip" className="flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/25 text-[11px] text-emerald-200">
            {chipText(f)}
            <button onClick={() => removeFilter(i)} className="text-emerald-300/70 hover:text-red-400"><X size={11} /></button>
          </span>
        ))}
        {filters.length > 0 && (
          <>
            <span className="text-[11px] text-slate-400">· <b className="text-white">{result?.rowCount?.toLocaleString() ?? "…"}</b> of {result?.totalRows?.toLocaleString() ?? dataset.analysis?.rowCount?.toLocaleString()} rows</span>
            <button data-testid="clear-all-filters" onClick={clearFilters} className="flex items-center gap-1 text-[11px] text-slate-400 hover:text-red-400 ml-1"><Eraser size={12} /> Clear all</button>
          </>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto p-4 sm:p-6 grid lg:grid-cols-3 gap-5">
          {/* LEFT */}
          <div className="lg:col-span-1 space-y-4">
            {/* Saved views */}
            <div className="bg-[#111827] border border-white/10 rounded-xl p-4" data-testid="saved-views">
              <div className="flex items-center gap-2 text-sm font-semibold text-white mb-2"><Bookmark size={15} className="text-emerald-400" /> Saved views</div>
              <div className="flex gap-2">
                <input data-testid="view-name-input" value={viewName} onChange={(e) => setViewName(e.target.value)} placeholder="Name this view…"
                  className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200" />
                <button data-testid="save-view-button" onClick={saveView} className="px-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white flex items-center gap-1.5 text-sm"><Save size={14} /></button>
              </div>
              {views.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {views.map((v) => (
                    <span key={v.id} data-testid="saved-view-chip" className="flex items-center gap-1 px-2 py-1 rounded-lg bg-white/5 border border-white/10 text-[11px] text-slate-300">
                      <button onClick={() => applyView(v)} className="hover:text-emerald-300">{v.name}</button>
                      <button onClick={() => deleteView(v.id)} className="text-slate-500 hover:text-red-400"><Trash2 size={11} /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* NL filter */}
            <div className="bg-gradient-to-br from-emerald-500/10 to-blue-500/5 border border-emerald-500/20 rounded-xl p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-white mb-2"><Wand2 size={15} className="text-emerald-400" /> Filter in plain language</div>
              <div className="flex gap-2">
                <input data-testid="nl-filter-input" value={nlText} onChange={(e) => setNlText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && applyNl()}
                  placeholder='e.g. "sales 1 lakh se zyada", "North region electronics"'
                  className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200" />
                <button data-testid="nl-filter-apply" onClick={applyNl} disabled={nlBusy} className="w-10 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center disabled:opacity-40">
                  {nlBusy ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={15} />}
                </button>
              </div>
            </div>

            {/* Excel-like filters */}
            <div className="bg-[#111827] border border-white/10 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-white"><Filter size={15} className="text-emerald-400" /> Column filters</div>
                <div className="flex items-center gap-1 text-[11px]">
                  {["AND", "OR"].map((l) => (
                    <button key={l} data-testid={`filter-logic-${l.toLowerCase()}`} onClick={() => setLogic(l)}
                      className={`px-2 py-1 rounded-md ${logic === l ? "bg-emerald-500/20 text-emerald-300" : "bg-white/5 text-slate-500"}`}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                {filters.map((f, i) => {
                  const ops = metaOf(f.column).operators;
                  return (
                    <div key={i} className="bg-[#0B0F17] border border-white/10 rounded-lg p-2 space-y-1.5" data-testid="filter-row">
                      <div className="flex gap-1.5">
                        <select data-testid="filter-column" value={f.column} onChange={(e) => onColChange(i, e.target.value)} className="ds-fi flex-1">
                          {columnMeta.map((c) => <option key={c.name} value={c.name} className="bg-[#111827]">{c.name}</option>)}
                        </select>
                        <select data-testid="filter-operator" value={f.operator} onChange={(e) => onOpChange(i, e.target.value)} className="ds-fi w-28">
                          {ops.map((o) => <option key={o} value={o} className="bg-[#111827]">{OP_LABEL[o]}</option>)}
                        </select>
                        <button onClick={() => removeFilter(i)} className="text-slate-500 hover:text-red-400 px-1"><Trash2 size={14} /></button>
                      </div>
                      {renderValueInput(f, i)}
                    </div>
                  );
                })}
              </div>
              <button data-testid="add-filter-button" onClick={addFilter} className="w-full mt-2 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-dashed border-white/15 text-xs text-slate-400 hover:text-emerald-300 hover:border-emerald-500/40 transition">
                <Plus size={14} /> Add filter
              </button>
            </div>

            {/* Aggregation */}
            <div className="bg-[#111827] border border-white/10 rounded-xl p-4 space-y-2.5">
              <div className="text-sm font-semibold text-white mb-1">Aggregation</div>
              <Field label="Group by (dimension)">
                <select data-testid="dimension-select" value={dimension} onChange={(e) => setDimension(e.target.value)} className="ds-fi">
                  <option value="" className="bg-[#111827]">— none —</option>
                  {dimensions.map((d) => <option key={d} value={d} className="bg-[#111827]">{d}</option>)}
                </select>
              </Field>
              <Field label="Measure">
                <select data-testid="measure-select" value={measure} onChange={(e) => setMeasure(e.target.value)} className="ds-fi">
                  <option value="" className="bg-[#111827]">— none —</option>
                  {measures.map((m) => <option key={m} value={m} className="bg-[#111827]">{m}</option>)}
                </select>
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Function">
                  <select data-testid="agg-select" value={agg} onChange={(e) => setAgg(e.target.value)} className="ds-fi">
                    {["sum", "avg", "count", "min", "max"].map((a) => <option key={a} value={a} className="bg-[#111827]">{a}</option>)}
                  </select>
                </Field>
                <Field label="Order">
                  <select value={order} onChange={(e) => setOrder(e.target.value)} className="ds-fi">
                    <option value="desc" className="bg-[#111827]">High → Low</option>
                    <option value="asc" className="bg-[#111827]">Low → High</option>
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Top N"><input type="number" value={topN} onChange={(e) => setTopN(e.target.value)} className="ds-fi" /></Field>
                <Field label="Chart">
                  <div className="flex gap-1">
                    {[["bar", BarChart3], ["line", LineIcon], ["pie", PieIcon]].map(([t, Ic]) => (
                      <button key={t} data-testid={`chart-${t}`} onClick={() => setChartType(t)}
                        className={`flex-1 py-1.5 rounded-md border flex items-center justify-center ${chartType === t ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-300" : "bg-white/5 border-white/10 text-slate-500"}`}>
                        <Ic size={14} />
                      </button>
                    ))}
                  </div>
                </Field>
              </div>
            </div>

            {/* NL analysis */}
            <div className="bg-[#111827] border border-white/10 rounded-xl p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-white mb-2"><Sparkles size={15} className="text-emerald-400" /> Ask about the filtered data</div>
              <div className="flex gap-2">
                <input data-testid="nl-question-input" value={question} onChange={(e) => setQuestion(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()}
                  placeholder="e.g. top 5 by revenue…" className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200" />
                <button data-testid="nl-ask-button" onClick={ask} disabled={analyzing} className="w-10 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center disabled:opacity-40">
                  {analyzing ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
                </button>
              </div>
            </div>
            {/* Pivot / cross-tab */}
            <div className="bg-[#111827] border border-white/10 rounded-xl p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-white mb-2"><LayoutGrid size={15} className="text-emerald-400" /> Pivot / cross-tab</div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Rows">
                  <select data-testid="pivot-rows" className="ds-fi" value={pivot.rows} onChange={(e) => setPivot((p) => ({ ...p, rows: e.target.value }))}>
                    <option value="" className="bg-[#111827]">—</option>
                    {dimensions.map((d) => <option key={d} value={d} className="bg-[#111827]">{d}</option>)}
                  </select>
                </Field>
                <Field label="Columns (optional)">
                  <select data-testid="pivot-cols" className="ds-fi" value={pivot.cols} onChange={(e) => setPivot((p) => ({ ...p, cols: e.target.value }))}>
                    <option value="" className="bg-[#111827]">—</option>
                    {dimensions.map((d) => <option key={d} value={d} className="bg-[#111827]">{d}</option>)}
                  </select>
                </Field>
                <Field label="Value">
                  <select data-testid="pivot-value" className="ds-fi" value={pivot.value} onChange={(e) => setPivot((p) => ({ ...p, value: e.target.value }))}>
                    <option value="" className="bg-[#111827]">—</option>
                    {measures.map((m) => <option key={m} value={m} className="bg-[#111827]">{m}</option>)}
                  </select>
                </Field>
                <Field label="Aggregation">
                  <select className="ds-fi" value={pivot.agg} onChange={(e) => setPivot((p) => ({ ...p, agg: e.target.value }))}>
                    {["sum", "avg", "count", "min", "max", "median"].map((a) => <option key={a} value={a} className="bg-[#111827]">{a}</option>)}
                  </select>
                </Field>
              </div>
              <button data-testid="run-pivot-button" onClick={runPivot} disabled={pivot.busy} className="w-full mt-2 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-40">
                {pivot.busy ? <Loader2 size={15} className="animate-spin" /> : <LayoutGrid size={15} />} Build pivot
              </button>
            </div>
          </div>

          {/* RIGHT */}
          <div className="lg:col-span-2 space-y-5">
            {busy && !result && <div className="text-slate-500 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Computing…</div>}
            {result && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {result.kpis.map((k, i) => (
                    <div key={i} data-testid="data-studio-kpi-card" className="bg-[#1F2937]/80 border border-white/10 rounded-xl p-4 hover:border-emerald-500/40 transition">
                      <div className="text-[11px] text-slate-500 uppercase tracking-wide">{k.title}</div>
                      <div className="text-xl font-head font-bold text-white mt-1 truncate">{k.value}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5 truncate">{k.subtitle}</div>
                    </div>
                  ))}
                </div>

                <div className="bg-[#111827] border border-white/10 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-xs text-slate-400">
                      {result.rowCount.toLocaleString()} of {result.totalRows.toLocaleString()} rows
                      {result.aggregation?.dimension && <> · {agg}({measure}) by {result.aggregation.dimension}</>}
                    </div>
                    <button data-testid="toggle-data-table" onClick={() => setShowTable((v) => !v)} className="text-[11px] text-slate-400 hover:text-emerald-300 flex items-center gap-1"><TableIcon size={12} /> {showTable ? "Hide" : "View"} rows</button>
                  </div>
                  {chartData.length > 0 && yKey ? (
                    <ResponsiveContainer width="100%" height={300}>
                      {chartType === "bar" ? (
                        <BarChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                          <XAxis dataKey={result.chart.xKey} tick={{ fill: "#94A3B8", fontSize: 11 }} />
                          <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} />
                          <Tooltip contentStyle={{ background: "#0B0F17", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} />
                          <Bar dataKey={yKey} radius={[6, 6, 0, 0]}>{chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar>
                        </BarChart>
                      ) : chartType === "line" ? (
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                          <XAxis dataKey={result.chart.xKey} tick={{ fill: "#94A3B8", fontSize: 11 }} />
                          <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} />
                          <Tooltip contentStyle={{ background: "#0B0F17", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} />
                          <Line type="monotone" dataKey={yKey} stroke="#10B981" strokeWidth={2.5} dot={{ fill: "#10B981" }} />
                        </LineChart>
                      ) : (
                        <PieChart>
                          <Pie data={chartData} dataKey={yKey} nameKey={result.chart.xKey} cx="50%" cy="50%" outerRadius={110} label>
                            {chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                          </Pie>
                          <Legend wrapperStyle={{ fontSize: 11, color: "#94A3B8" }} />
                          <Tooltip contentStyle={{ background: "#0B0F17", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} />
                        </PieChart>
                      )}
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-slate-500 text-sm py-8 text-center">Select a dimension and a measure to visualize.</div>
                  )}
                </div>

                {result.aggregation?.rows?.length > 0 && (
                  <div className="bg-[#111827] border border-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-sm font-semibold text-white mb-3"><TableIcon size={15} className="text-emerald-400" /> Aggregated result</div>
                    <div className="overflow-x-auto rounded-lg border border-white/10">
                      <table className="w-full text-sm">
                        <thead className="bg-white/5 text-slate-300"><tr>{result.table.columns.map((c) => <th key={c} className="px-3 py-2 text-left font-semibold border-b border-white/10">{c}</th>)}</tr></thead>
                        <tbody>
                          {result.table.rows.map((r, i) => (
                            <tr key={i} className="hover:bg-white/5">{result.table.columns.map((c) => <td key={c} className="px-3 py-2 border-b border-white/5 text-slate-300">{typeof r[c] === "number" ? r[c].toLocaleString() : String(r[c])}</td>)}</tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {showTable && result.tableRows?.length > 0 && (
                  <div className="bg-[#111827] border border-white/10 rounded-xl p-4" data-testid="filtered-rows-table">
                    <div className="text-xs text-slate-400 mb-2">Filtered rows — showing first {result.tableRows.length} of {result.rowCount.toLocaleString()} (original {result.totalRows.toLocaleString()})</div>
                    <div className="overflow-x-auto rounded-lg border border-white/10 max-h-96">
                      <table className="w-full text-xs">
                        <thead className="bg-white/5 text-slate-300 sticky top-0"><tr>{Object.keys(result.tableRows[0]).map((c) => <th key={c} className="px-2 py-1.5 text-left font-semibold border-b border-white/10 whitespace-nowrap">{c}</th>)}</tr></thead>
                        <tbody>
                          {result.tableRows.map((r, i) => (
                            <tr key={i} className="hover:bg-white/5">{Object.keys(result.tableRows[0]).map((c) => <td key={c} className="px-2 py-1.5 border-b border-white/5 text-slate-400 whitespace-nowrap">{r[c] === null ? "—" : String(r[c])}</td>)}</tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}

            {pivot.result && pivot.result.columns?.length > 0 && (
              <div className="bg-[#111827] border border-white/10 rounded-xl p-4" data-testid="pivot-result">
                <div className="flex items-center gap-2 text-sm font-semibold text-white mb-2"><LayoutGrid size={15} className="text-emerald-400" /> Pivot — {pivot.agg}({pivot.value}) · {pivot.result.rowsAnalyzed?.toLocaleString()} of {pivot.result.totalRows?.toLocaleString()} rows</div>
                {pivot.result.note ? <div className="text-xs text-slate-500">{pivot.result.note}</div> : (
                  <div className="overflow-x-auto rounded-lg border border-white/10 max-h-96">
                    <table className="w-full text-xs">
                      <thead className="bg-white/5 text-slate-300 sticky top-0"><tr>{pivot.result.columns.map((c) => <th key={c} className="px-2 py-1.5 text-left font-semibold border-b border-white/10 whitespace-nowrap">{c}</th>)}</tr></thead>
                      <tbody>
                        {pivot.result.rows.map((r, i) => (
                          <tr key={i} className="hover:bg-white/5">{pivot.result.columns.map((c) => <td key={c} className="px-2 py-1.5 border-b border-white/5 text-slate-400 whitespace-nowrap">{typeof r[c] === "number" ? r[c].toLocaleString() : String(r[c] ?? "—")}</td>)}</tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {analysisRes && (
              <div className="bg-gradient-to-br from-emerald-500/10 to-blue-500/5 border border-emerald-500/20 rounded-xl p-5 space-y-3" data-testid="analysis-result">
                <div className="text-[11px] uppercase tracking-wide text-emerald-400">
                  {analysisRes.operation} · computed on {analysisRes.rowCount?.toLocaleString()} of {analysisRes.totalRows?.toLocaleString()} rows {analysisRes.aiConfigured ? "· AI narration" : "· deterministic"}
                </div>
                {analysisRes.scalar && Object.keys(analysisRes.scalar).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(analysisRes.scalar).map(([k, v]) => (
                      <div key={k} className="px-3 py-2 rounded-lg bg-white/5 border border-white/10">
                        <div className="text-[10px] text-slate-500 uppercase">{k}</div>
                        <div className="text-lg font-head font-bold text-white">{typeof v === "number" ? v.toLocaleString() : String(v)}</div>
                      </div>
                    ))}
                  </div>
                )}
                {analysisRes.chart?.data?.length > 0 && analysisRes.chart.yKey && (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={analysisRes.chart.data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey={analysisRes.chart.xKey} tick={{ fill: "#94A3B8", fontSize: 11 }} />
                      <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} />
                      <Tooltip contentStyle={{ background: "#0B0F17", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} />
                      <Bar dataKey={analysisRes.chart.yKey} radius={[6, 6, 0, 0]}>{analysisRes.chart.data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
                {analysisRes.resultRows?.length > 0 && analysisRes.resultColumns?.length > 0 && (
                  <div className="overflow-x-auto rounded-lg border border-white/10 max-h-72">
                    <table className="w-full text-xs">
                      <thead className="bg-white/5 text-slate-300 sticky top-0"><tr>{analysisRes.resultColumns.map((c) => <th key={c} className="px-2 py-1.5 text-left font-semibold border-b border-white/10 whitespace-nowrap">{c}</th>)}</tr></thead>
                      <tbody>
                        {analysisRes.resultRows.slice(0, 100).map((r, i) => (
                          <tr key={i} className="hover:bg-white/5">{analysisRes.resultColumns.map((c) => <td key={c} className="px-2 py-1.5 border-b border-white/5 text-slate-400 whitespace-nowrap">{r[c] === null || r[c] === undefined ? "—" : (typeof r[c] === "number" ? r[c].toLocaleString() : String(r[c]))}</td>)}</tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {analysisRes.note && <div className="text-[11px] text-slate-500">{analysisRes.note}</div>}
                {analysisRes.narration && <Markdown>{analysisRes.narration}</Markdown>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (<label className="block"><span className="block text-[11px] text-slate-500 mb-1">{label}</span>{children}</label>);
}
